import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { PlatformShell } from './PlatformShell';
import { RONDO_MENU, type AppView, type MenuItem, type SubItem } from './platform-menu';
import { computeAllowedModuleKeys } from './allowed-module-keys';
import { TasksPage } from '../tasks/TasksPage';
import { HelpdeskPage } from '../helpdesk/HelpdeskPage';
import { SettingsPage } from '../settings/SettingsPage';
import { CronyPage } from '../crony/CronyPage';
import { UsersSeznamPage } from '../rondo/UsersSeznamPage';
import { UsersPrehledPage } from '../rondo/UsersPrehledPage';
import { UsersVyplatyPage } from '../rondo/UsersVyplatyPage';
import { UserDetailPage } from '../rondo/UserDetailPage';
import { SegmentacePage } from '../rondo/SegmentacePage';
import { ProduktyPrehledPage } from '../rondo/ProduktyPrehledPage';
import { ProduktySouvezePage } from '../rondo/ProduktySouvezePage';
import { ProduktyHryPage } from '../rondo/ProduktyHryPage';
import { ProduktyTipovackaPage } from '../rondo/ProduktyTipovackaPage';
import { FinanceUzivatelePage } from '../rondo/FinanceUzivatelePage';
import { FinanceTrzbyPage } from '../rondo/FinanceTrzbyPage';
import { FinanceTrzbyRealPage } from '../rondo/FinanceTrzbyRealPage';
import { FinanceReklamkyPage } from '../rondo/FinanceReklamkyPage';
import { FinanceAffiliatesPage } from '../rondo/FinanceAffiliatesPage';
import { AktivitaPrehledPage } from '../rondo/aktivita/AktivitaPrehledPage';
import {
  AktivitaNavstevnostPage,
  AktivitaZobrazeniPage,
  AktivitaCasPage,
  AktivitaOpusteniPage,
} from '../rondo/aktivita/AktivitaDrillPage';
import { AktivitaProduktyPage } from '../rondo/aktivita/AktivitaProduktyPage';
import { AktivitaSeoPage } from '../rondo/aktivita/AktivitaSeoPage';
import { AktivitaVyhledavaniPage } from '../rondo/aktivita/AktivitaVyhledavaniPage';
import { AktivitaBehavioralPage } from '../rondo/aktivita/AktivitaBehavioralPage';
import { MarketingPrehledPage } from '../rondo/marketing/MarketingPrehledPage';
import { MarketingKampanePage } from '../rondo/marketing/MarketingKampanePage';
import { MarketingKanalyPage } from '../rondo/marketing/MarketingKanalyPage';
import { MarketingSablonyPage } from '../rondo/marketing/MarketingSablonyPage';
import { RondoNavProvider } from '../rondo/RondoNav';
import { RondoDashboardPage } from '../rondo/RondoDashboardPage';
import { useScrollRestore } from './useScrollRestore';

const RONDO_PAGES: Record<string, () => JSX.Element | null> = {
  'uzivatele-prehled': UsersPrehledPage,
  'uzivatele-seznam': UsersSeznamPage,
  'uzivatele-vyplaty': UsersVyplatyPage,
  'uzivatele-segmentace': SegmentacePage,
  'produkty-prehled': ProduktyPrehledPage,
  'produkty-souteze': ProduktySouvezePage,
  'produkty-hry': ProduktyHryPage,
  'produkty-tipovacka': ProduktyTipovackaPage,
  'finance-uzivatele': FinanceUzivatelePage,
  'finance-affiliates': FinanceAffiliatesPage,
  'finance-trzby': FinanceTrzbyPage,
  'finance-trzby-real': FinanceTrzbyRealPage,
  'finance-reklamky': FinanceReklamkyPage,
  'rondo-aktivita': AktivitaPrehledPage,
  'aktivita-navstevnost': AktivitaNavstevnostPage,
  'aktivita-zobrazeni': AktivitaZobrazeniPage,
  'aktivita-cas': AktivitaCasPage,
  'aktivita-opusteni': AktivitaOpusteniPage,
  'aktivita-produkty': AktivitaProduktyPage,
  'aktivita-seo': AktivitaSeoPage,
  'aktivita-vyhledavani': AktivitaVyhledavaniPage,
  'marketing-behavioral': AktivitaBehavioralPage,
  'marketing-prehled': MarketingPrehledPage,
  'marketing-kampane': MarketingKampanePage,
  'marketing-kanaly': MarketingKanalyPage,
  'marketing-sablony': MarketingSablonyPage,
};

type Route =
  | { kind: 'module'; moduleId: string; childId?: string }
  | { kind: 'settings' }
  | { kind: 'tasks' }
  | { kind: 'helpdesk' }
  | { kind: 'crony' };

function routeToPath(
  route: Route,
  userDetailId: number | null,
  taskDetailId: number | null,
): string {
  if (route.kind === 'settings') return '/nastaveni';
  if (route.kind === 'crony') return '/crony';
  if (route.kind === 'tasks') {
    return taskDetailId != null ? `/ukoly?taskId=${taskDetailId}` : '/ukoly';
  }
  if (route.kind === 'helpdesk') return '/helpdesk';
  if (route.moduleId === 'dashboard') return '/';
  let path = '/';
  if (route.childId) {
    const mod = RONDO_MENU.find((m) => m.id === route.moduleId);
    const child = mod?.children?.find((c) => c.id === route.childId);
    path = child?.path ?? '/';
  }
  if (userDetailId != null && route.moduleId === 'uzivatele') {
    path += `?detail=${userDetailId}`;
  }
  return path;
}

function pathToState(pathname: string, search: string): {
  route: Route;
  userDetailId: number | null;
  taskDetailId: number | null;
} {
  if (!pathname || pathname === '/') {
    return { route: { kind: 'module', moduleId: 'dashboard' }, userDetailId: null, taskDetailId: null };
  }
  if (pathname === '/nastaveni') {
    return { route: { kind: 'settings' }, userDetailId: null, taskDetailId: null };
  }
  if (pathname === '/crony') {
    return { route: { kind: 'crony' }, userDetailId: null, taskDetailId: null };
  }
  if (pathname === '/ukoly') {
    const rawTask = new URLSearchParams(search).get('taskId');
    const taskDetailId =
      rawTask != null && rawTask !== '' && !Number.isNaN(Number(rawTask)) ? Number(rawTask) : null;
    return { route: { kind: 'tasks' }, userDetailId: null, taskDetailId };
  }
  if (pathname === '/helpdesk') {
    return { route: { kind: 'helpdesk' }, userDetailId: null, taskDetailId: null };
  }
  for (const mod of RONDO_MENU) {
    const child = mod.children?.find((c) => c.path === pathname || c.legacyPath === pathname);
    if (child) {
      const raw = new URLSearchParams(search).get('detail');
      const detail = raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : null;
      return {
        route: { kind: 'module', moduleId: mod.id, childId: child.id },
        userDetailId: detail,
        taskDetailId: null,
      };
    }
  }
  return { route: { kind: 'module', moduleId: 'dashboard' }, userDetailId: null, taskDetailId: null };
}

// Efektivní modulový přístup pro AKTIVNÍ Space → množina povolených katalogových
// menu-klíčů. null = bez omezení (tenant-admin / stará session bez moduleAccess →
// fail-open na UI; backend gate platí vždy).
export function AppShell() {
  const { me } = useAuth();
  // Initial route is derived from the URL (deep-link friendly) - lazy init so
  // the first render already matches the path, avoiding a sync-effect race that
  // would otherwise clobber the URL back to '/'.
  const [route, setRoute] = useState<Route>(
    () => pathToState(window.location.pathname, window.location.search).route,
  );
  const [userDetailId, setUserDetailId] = useState<number | null>(
    () => pathToState(window.location.pathname, window.location.search).userDetailId,
  );
  const [taskDetailId, setTaskDetailId] = useState<number | null>(
    () => pathToState(window.location.pathname, window.location.search).taskDetailId,
  );

  useEffect(() => {
    const p0 = routeToPath(route, userDetailId, taskDetailId);
    const [pPath, pQuery] = p0.split('?');
    let p = p0;
    // Zůstáváme na stejné path → zachovej page-level taby (deep-link ?tab/?sub/
    // ?ctab) i ostatní page query (Šablony ?tab=mail). Shell řídí path + vlastní
    // ?detail/?taskId; bez tohoto by se ?tab strhával při každém routovacím efektu
    // (a u detailu usera, kde p už ?detail obsahuje, by se taby ztratily na load).
    if (pPath === window.location.pathname) {
      const cur = new URLSearchParams(window.location.search);
      const next = new URLSearchParams(pQuery || '');
      for (const key of ['tab', 'sub', 'ctab']) {
        const v = cur.get(key);
        if (v != null && !next.has(key)) next.set(key, v);
      }
      // Pokud kanonická cesta nemá vlastní query, zachovej i zbytek page query
      // (kromě shellem řízených ?detail/?taskId - ty by jinak visely i po odchodu).
      if (!pQuery) {
        cur.delete('detail');
        cur.delete('taskId');
        for (const [k, v] of cur) if (!next.has(k)) next.set(k, v);
      }
      const qs = next.toString();
      p = pPath + (qs ? `?${qs}` : '');
    }
    if (p !== window.location.pathname + window.location.search) {
      window.history.pushState(null, '', p);
    }
  }, [route, userDetailId, taskDetailId]);

  useEffect(() => {
    function onPopState() {
      const { route: r, userDetailId: d, taskDetailId: t } = pathToState(
        window.location.pathname,
        window.location.search,
      );
      setRoute(r);
      setUserDetailId(d);
      setTaskDetailId(t);
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useScrollRestore(`${routeToPath(route, userDetailId, taskDetailId)}`);

  const allowedModuleKeys = computeAllowedModuleKeys(me);
  const visibleMenu =
    allowedModuleKeys === null
      ? RONDO_MENU
      : RONDO_MENU.filter((item) => allowedModuleKeys.has(item.id));
  const canSeeTasks = allowedModuleKeys === null || allowedModuleKeys.has('ukoly');
  const canSeeHelpdesk = allowedModuleKeys === null || allowedModuleKeys.has('helpdesk');
  // Crony = ops přehled cronů/synců napříč spaci → jen tenant/super-admin
  // (allowedModuleKeys===null). API je gate na `admin:users:manage`.
  const canSeeCrony = allowedModuleKeys === null;

  useEffect(() => {
    if (allowedModuleKeys === null) return;
    const onAllowed =
      (route.kind === 'module' && allowedModuleKeys.has(route.moduleId)) ||
      (route.kind === 'tasks' && canSeeTasks) ||
      (route.kind === 'helpdesk' && canSeeHelpdesk) ||
      route.kind === 'settings' ||
      (route.kind === 'crony' && canSeeCrony);
    if (onAllowed) return;
    const firstModule = visibleMenu[0];
    if (firstModule) {
      const first = firstModule.children?.[0];
      setRoute(
        first
          ? { kind: 'module', moduleId: firstModule.id, childId: first.id }
          : { kind: 'module', moduleId: firstModule.id },
      );
    } else if (canSeeTasks) {
      setRoute({ kind: 'tasks' });
    } else if (canSeeHelpdesk) {
      setRoute({ kind: 'helpdesk' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, allowedModuleKeys, canSeeTasks, canSeeHelpdesk, canSeeCrony]);

  if (!me) return null;

  function selectModule(item: MenuItem) {
    setUserDetailId(null);
    setTaskDetailId(null);
    const first = item.children?.[0];
    if (first) {
      setRoute({ kind: 'module', moduleId: item.id, childId: first.id });
    } else {
      setRoute({ kind: 'module', moduleId: item.id });
    }
  }

  function selectChild(item: MenuItem, child: SubItem) {
    setUserDetailId(null);
    setTaskDetailId(null);
    setRoute({ kind: 'module', moduleId: item.id, childId: child.id });
  }

  function openTasks() {
    setUserDetailId(null);
    setTaskDetailId(null);
    setRoute({ kind: 'tasks' });
  }

  function openTask(taskId: number) {
    setUserDetailId(null);
    setRoute({ kind: 'tasks' });
    setTaskDetailId(taskId);
  }

  function openHelpdesk() {
    setUserDetailId(null);
    setTaskDetailId(null);
    setRoute({ kind: 'helpdesk' });
  }

  function openSettings() {
    setRoute({ kind: 'settings' });
  }

  function openCrony() {
    setRoute({ kind: 'crony' });
  }

  const activeModuleId = route.kind === 'module' ? route.moduleId : '';
  const activeChildId = route.kind === 'module' ? route.childId : undefined;
  const isTasksActive = route.kind === 'tasks';
  const isHelpdeskActive = route.kind === 'helpdesk';

  const module = RONDO_MENU.find((m) => m.id === activeModuleId) ?? null;
  const child = module?.children?.find((c) => c.id === activeChildId) ?? null;
  const view: AppView | 'settings-page' =
    route.kind === 'settings'
      ? 'settings-page'
      : route.kind === 'tasks'
        ? 'tasks'
        : (child?.view ?? module?.view ?? 'placeholder');
  const placeholderLabel = child?.label ?? module?.label ?? '';

  const RondoPage = activeChildId ? RONDO_PAGES[activeChildId] : undefined;

  const pageTitle =
    route.kind === 'helpdesk'
      ? 'Helpdesk'
      : view === 'tasks'
      ? 'Úkoly'
      : view === 'dashboard'
        ? 'Dashboard'
        : RondoPage
          ? placeholderLabel
          : undefined;

  const rondoNav = {
    openUserDetail: (id: number) => setUserDetailId(id),
    closeUserDetail: () => setUserDetailId(null),
  };
  const detailActive =
    userDetailId != null && route.kind === 'module' && route.moduleId === 'uzivatele';

  return (
    <RondoNavProvider value={rondoNav}>
      <PlatformShell
        arcRefresh
        menu={visibleMenu}
        showTasks={canSeeTasks}
        showHelpdesk={canSeeHelpdesk}
        currentSpaceSlug="rondo"
        activeModuleId={activeModuleId}
        activeChildId={activeChildId}
        isTasksActive={isTasksActive}
        isHelpdeskActive={isHelpdeskActive}
        onSelectModule={selectModule}
        onSelectChild={selectChild}
        onOpenTasks={openTasks}
        onOpenTask={openTask}
        onOpenHelpdesk={openHelpdesk}
        onOpenSettings={openSettings}
        onOpenCrony={openCrony}
        showCrony={canSeeCrony}
        pageTitle={detailActive ? undefined : pageTitle}
      >
        {detailActive ? (
          <UserDetailPage
            id={userDetailId as number}
            onBack={() => setUserDetailId(null)}
            backLabel={activeChildId === 'uzivatele-vyplaty' ? 'Zpět na výplaty' : activeChildId === 'uzivatele-prehled' ? 'Zpět na přehled' : undefined}
          />
        ) : route.kind === 'crony' ? (
          <CronyPage />
        ) : view === 'settings-page' ? (
          <SettingsPage />
        ) : view === 'tasks' ? (
          <TasksPage selectedTaskId={taskDetailId} onSelectTask={setTaskDetailId} />
        ) : route.kind === 'helpdesk' ? (
          <HelpdeskPage />
        ) : RondoPage ? (
          <RondoPage />
        ) : view === 'dashboard' ? (
          <RondoDashboardPage />
        ) : (
          <ComingSoon label={placeholderLabel} />
        )}
      </PlatformShell>
    </RondoNavProvider>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <section><header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">RONDO</div><h1>{label}</h1></div></header><div className="prototype-panel prototype-empty-state"><div className="prototype-empty-icon">◇</div><h2>Tato stránka se připravuje</h2><p>Obsah této části zatím není dostupný.</p></div></section>
  );
}
